import { Button, InputAdornment } from "@mui/material";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import { useSelector } from "react-redux";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { BID_DATE_TYPE, BID_STATUS } from "../../util/bid/bidConstants.js";
import { getMultiStationAdminStationList, sortOnCaseSensitiveTexts } from "../../util/commonUtils";
import { MULTI_STATION_ADMIN_ACCESS, STATION } from "../../util/constants.js";
import { THREE_YEARS_IN_PAST } from "../../util/dateHelper";
import { convert_Luxon_To_DateFns } from "../../util/dateTimeFormatter.js";
import {
  BID_TYPE_FILTER_MUST_BE_SELECTED,
  DATE_TYPE_FILTER_MUST_BE_SELECTED,
  STATION_FILTER_MUST_BE_SELECTED,
  STATUS_FILTER_MUST_BE_SELECTED,
  WORKGROUP_FILTER_MUST_BE_SELECTED,
} from "../../util/messageConstants.js";
import CancelIconButton from "../fundamentals/CancelIconButton.jsx";
import SearchButton from "../fundamentals/SearchButton.jsx";
import StarsDatePicker from "../fundamentals/StarsDatePicker.jsx";
import StarsSelectInput from "../fundamentals/StarsSelectInput.jsx";

const useStyles = makeStyles((theme) => ({
  filterInputs: {
    width: "100%",
    textAlign: "left",
  },
  topRow: {
    marginTop: "5px",
  },
  title: {
    fontSize: "30px",
    textAlign: "center",
    width: "200px",
    margin: "auto",
    marginTop: "25px",
    whiteSpace: "pre-line",
  },
}));

const BidAdminFilterForm = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);

  const renderDateTypeSelectList = () => {
    return (
      <StarsSelectInput
        label="Date Type"
        menuMessage="Filter on Date Type"
        value={props.dateTypeFilter}
        allOptions={Object.values(BID_DATE_TYPE)}
        sortComparator={sortOnCaseSensitiveTexts}
        onChange={props.onDateTypeFilterChange.bind(this)}
        errors={props.dateTypeFilter === "" ? DATE_TYPE_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        fullWidth={true}
      />
    );
  };

  const renderBidTypeSelectList = () => {
    return (
      <StarsSelectInput
        label="Bid Type"
        id="bidTypeSelectElement"
        menuMessage="Filter on Bid Type"
        value={props.bidTypeFilters}
        allOptions={Object.values(props.allBidTypesFilters)}
        sortComparator={sortOnCaseSensitiveTexts}
        errors={props.bidTypeFilters?.length <= 0 ? BID_TYPE_FILTER_MUST_BE_SELECTED : ""}
        className={classes.inputSelect}
        onChange={props.onBidTypeFilterChange.bind(this)}
        multiple={true}
        fullWidth={true}
      />
    );
  };

  const renderStartDatePicker = () => {
    return (
      <StarsDatePicker
        label="Start Date"
        date={props.startDateFilter}
        onChange={props.onStartDateFilterChange}
        dateErrors={props.startDateFilterErrors}
        minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.stationFilter))}
        className={classes.filterInputs}
        fullWidth={true}
      />
    );
  };

  const renderEndDatePicker = () => {
    return (
      <StarsDatePicker
        label="End Date"
        date={props.endDateFilter}
        onChange={props.onEndDateFilterChange}
        dateErrors={props.endDateFilterErrors}
        minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.stationFilter))}
        className={classes.filterInputs}
        fullWidth={true}
      />
    );
  };

  const renderBidStatusSelectList = () => {
    return (
      <StarsSelectInput
        label="Status"
        id="bidStatusSelectElement"
        menuMessage="Filter on Status"
        value={props.bidStatusFilters}
        allOptions={Object.values(BID_STATUS)}
        sortComparator={sortOnCaseSensitiveTexts}
        errors={props.bidStatusFilters?.length <= 0 ? STATUS_FILTER_MUST_BE_SELECTED : ""}
        className={classes.inputSelect}
        onChange={props.onStatusFilterChange.bind(this)}
        multiple={true}
        fullWidth={true}
      />
    );
  };

  const renderStationDropDown = () => {
    return (
      <>
        {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
          <Box>
            <StarsSelectInput
              label={STATION}
              id="stationFilterSelectElement"
              menuMessage={STATION}
              value={props.stationFilter}
              allOptions={getMultiStationAdminStationList(userSession)}
              sortComparator={sortOnCaseSensitiveTexts}
              onChange={props.onStationFilterChange}
              className={classes.filterInputs}
              fullWidth={true}
              isMenuItemDisabled={(station) => !props?.featureConfig?.userStations?.includes(station)}
              errors={props.stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
            />
          </Box>
        ) : (
          <Box>
            <TextField label="Station" variant="outlined" value={props.stationFilter} disabled />
          </Box>
        )}
      </>
    );
  };

  const renderWorkgroupFilterDropDown = () => {
    return (
      <StarsSelectInput
        label={"Workgroup"}
        id="workgroupFilterSelectElement"
        menuMessage="Filter on Workgroup"
        value={props.workgroupFilters ? props.workgroupFilters : []}
        allOptions={Object.values(props.allWorkgroupFilters)}
        sortComparator={sortOnCaseSensitiveTexts}
        onChange={props.onWorkgroupFilterChange.bind(this)}
        className={classes.filterInputs}
        errors={props.workgroupFilters.length <= 0 ? WORKGROUP_FILTER_MUST_BE_SELECTED : ""}
        fullWidth={true}
        multiple={true}
      />
    );
  };

  const renderSearchBar = () => {
    return (
      <FormControl className={classes.filterInputs}>
        <TextField
          label="Search"
          variant="outlined"
          value={props.searchString}
          onChange={props.onSearchChange.bind(this)}
          onBlur={props.onSearchBlur.bind(this)}
          InputProps={{
            endAdornment:
              props.searchString !== "" ? (
                <InputAdornment position="end">
                  <CancelIconButton onSearchCancel={props.onSearchCancel} />
                  <SearchButton onSearchSubmit={props.onSearchSubmit} searchString={props.searchString} />
                </InputAdornment>
              ) : (
                <InputAdornment position="end">
                  <SearchButton />
                </InputAdornment>
              ),
          }}
          onKeyUp={props.onSearchEnterKeyPressed.bind(this)}
          error={props.searchStringErrors !== ""}
          helperText={props.searchStringErrors}
        />
      </FormControl>
    );
  };

  return (
    <Grid container className={classes.topRow} data-testid="bid-filter-form">
      <Grid item xs={6}>
        <Typography sx={{ textAlign: "left", marginLeft: "2rem", marginTop: "10px", fontSize: "20px" }}>
          <Box>{props.title}</Box>
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            props.setIsCreateBidDialogOpen(true);
          }}
          sx={{ float: "right", marginRight: "2rem", height: "40px" }}
        >
          <p>Create New Bid</p>
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Grid container sx={{ paddingLeft: "2rem", paddingRight: "2rem", paddingTop: "1.5rem", paddingBottom: "1rem" }}>
          <Grid item xs={0.9}>
            <Box pr={1}>{renderStationDropDown()}</Box>
          </Grid>
          <Grid item xs={1.5}>
            <Box pr={1}>{renderWorkgroupFilterDropDown()}</Box>
          </Grid>
          <Grid item xs={1.2}>
            <Box pr={1}>{renderBidTypeSelectList()}</Box>
          </Grid>
          <Grid item xs={1.3}>
            <Box pr={1}>{renderBidStatusSelectList()}</Box>
          </Grid>
          <Grid item xs={1.9}>
            <Box pr={1}>{renderDateTypeSelectList()}</Box>
          </Grid>
          <Grid item xs={1.7}>
            <Box pr={1}>{renderStartDatePicker()}</Box>
          </Grid>
          <Grid item xs={1.7}>
            <Box pr={1}>{renderEndDatePicker()}</Box>
          </Grid>
          <Grid item xs={1.8}>
            <Box>{renderSearchBar()}</Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

BidAdminFilterForm.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  stationFilter: PropTypes.string.isRequired,
  onStationFilterChange: PropTypes.func.isRequired,
  allWorkgroupFilters: PropTypes.array.isRequired,
  workgroupFilters: PropTypes.array.isRequired,
  onWorkgroupFilterChange: PropTypes.func.isRequired,
  isWorkgroupSelectAllChecked: PropTypes.func.isRequired,
  isWorkgroupSelectAllIndeterminate: PropTypes.func.isRequired,
  dateTypeFilter: PropTypes.string.isRequired,
  onDateTypeFilterChange: PropTypes.func.isRequired,
  bidStatusFilters: PropTypes.array.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  startDateFilter: PropTypes.object.isRequired,
  startDateFilterErrors: PropTypes.string.isRequired,
  onStartDateFilterChange: PropTypes.func.isRequired,
  endDateFilter: PropTypes.object.isRequired,
  endDateFilterErrors: PropTypes.string.isRequired,
  onEndDateFilterChange: PropTypes.func.isRequired,
  bidTypeFilters: PropTypes.array.isRequired,
  allBidTypesFilters: PropTypes.array.isRequired,
  onBidTypeFilterChange: PropTypes.func.isRequired,
  searchString: PropTypes.string,
  searchStringErrors: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchBlur: PropTypes.func.isRequired,
  onSearchEnterKeyPressed: PropTypes.func.isRequired,
  onSearchCancel: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  setIsCreateBidDialogOpen: PropTypes.func.isRequired,
};

export default BidAdminFilterForm;
